import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Box, CircularProgress, Divider, Popover, Typography } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { withStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';

import Button from '../../button';
import { useDownloadExportRequest, useGetExportRequests } from '../../../queries/queryExportRequests';
import { parseStringDateToFormat } from '../../../utils/utils';
import { useDownloadCenter } from '../DownloadCenterContext';
import { DownloadCenterHistoryModal } from './DownloadCenterHistoryModal';
import { STATUS_LABEL, canDownload, readExportRequests } from './exportRequestUtils';
import styles from './DownloadCenterPopover.styles';

const DownloadCenterPopover = ({ classes, menuItemClassName, clientId }) => {
  const anchorEl = useRef(null);
  const [open, setOpen] = useState(false);
  const [isHistoryOpen, setHistoryOpen] = useState(false);
  const { registerOpenHandler } = useDownloadCenter();

  const { data, isLoading, isFetching, refetch } = useGetExportRequests({ clientId, refetchInBackground: true });
  const exportRequests = readExportRequests(data);

  const badgeCount = exportRequests.length;

  const downloadExportRequest = useDownloadExportRequest();

  // Abrir la bandeja pide el estado fresco. Lo cacheado por el polling se sigue mostrando mientras
  // la request viaja, y la lista se reemplaza cuando responde.
  const handleOpen = useCallback(() => {
    setOpen(true);
    refetch();
  }, [refetch]);

  useEffect(() => {
    registerOpenHandler(handleOpen);
  }, [registerOpenHandler, handleOpen]);

  const handleDownload = exportRequest =>
    downloadExportRequest.mutate({ clientId, exportRequestId: exportRequest.id, filename: exportRequest.filename });

  const handleSeeMore = () => {
    setOpen(false);
    setHistoryOpen(true);
  };

  return (
    <>
      <div ref={anchorEl}>
        <Badge badgeContent={badgeCount} color="error" overlap="rectangular" classes={{ badge: classes.badgeContent }}>
          <Button onClick={() => (open ? setOpen(false) : handleOpen())} color="primary" aria-haspopup="true" className={menuItemClassName}>
            <GetAppIcon fontSize="small" />
          </Button>
        </Badge>
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl.current}
        onClose={() => setOpen(false)}
        transitionDuration={{ enter: 75, exit: 50 }}
        PaperProps={{
          style: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            borderRadius: 4,
            overflow: 'visible'
          }
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box className={classes.root}>
          <Box className={classes.titleRow}>
            <Typography variant="subtitle1" className={classes.title}>
              <FormattedMessage id="downloadCenter.title" defaultMessage="Descargas" />
            </Typography>
            {/* Solo en el refetch con datos ya en pantalla; la primera carga usa el spinner grande. */}
            {isFetching && !isLoading && <CircularProgress size={14} thickness={5} className={classes.refreshSpinner} />}
          </Box>
          <Typography variant="caption" className={classes.subtitle}>
            <FormattedMessage id="downloadCenter.subtitle" defaultMessage="Archivos generados recientemente" />
          </Typography>

          {isLoading ? (
            <Box className={classes.emptyState}>
              <CircularProgress size={24} />
            </Box>
          ) : exportRequests.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="body2">
                <FormattedMessage id="downloadCenter.empty" defaultMessage="Aún no tienes archivos generados." />
              </Typography>
            </Box>
          ) : (
            <>
              <Box className={classes.list}>
                {exportRequests.map((exportRequest, index) => (
                  <React.Fragment key={exportRequest.id}>
                    <Box className={classes.item}>
                      <Box className={`${classes.item}-info`}>
                        <Typography variant="body2" className={`${classes.item}-filename`}>
                          {exportRequest.displayName}
                        </Typography>
                        <Typography variant="caption" className={`${classes.item}-requested`}>
                          <FormattedMessage id="downloadCenter.requestedAt" defaultMessage="Solicitado" />:{' '}
                          {parseStringDateToFormat(exportRequest.requestedAt)}
                        </Typography>
                      </Box>
                      <Box className={`${classes.item}-side`}>
                        <Typography variant="caption" className={`${classes.item}-status ${classes.item}-status_${exportRequest.status}`}>
                          {STATUS_LABEL[exportRequest.status]}
                        </Typography>
                        {canDownload(exportRequest) && (
                          <Button variant="outlined" size="small" onClick={() => handleDownload(exportRequest)}>
                            <FormattedMessage id="downloadCenter.download" defaultMessage="Descargar" />
                          </Button>
                        )}
                      </Box>
                    </Box>
                    {index < exportRequests.length - 1 && <Divider className={`${classes.item}-divider`} />}
                  </React.Fragment>
                ))}
              </Box>
              <Box className={classes.seeMore}>
                <Button variant="text" size="small" onClick={handleSeeMore}>
                  <FormattedMessage id="downloadCenter.seeMore" defaultMessage="Ver más" />
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>
      <DownloadCenterHistoryModal open={isHistoryOpen} onClose={() => setHistoryOpen(false)} clientId={clientId} />
    </>
  );
};

DownloadCenterPopover.propTypes = {
  classes: PropTypes.object.isRequired,
  menuItemClassName: PropTypes.string,
  clientId: PropTypes.number.isRequired
};

export default withStyles(styles)(DownloadCenterPopover);
